import axios, { AxiosInstance, AxiosError } from 'axios';
import type { ApiResponse, User, Barter, BarterGroup, Transaction, Review, SupportTicket, SystemSettings, DashboardData } from '@/types';

const API_BASE_URL = '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          // Clear all auth-related data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('auth-storage');

          // Redirect to login
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url, data ? { data } : undefined);
    return response.data;
  }

  async upload<T>(url: string, formData: FormData): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const api = new ApiClient();

// Auth API
export const authApi = {
  login: (email: string, password: string, recaptchaToken?: string | null) =>
    api.post<{ user: User; token: string; otp_required?: boolean; user_id?: number; expires_in_minutes?: number }>('/auth/login', {
      email,
      password,
      ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
    }),
  verifyOtp: (userId: number, otpCode: string) =>
    api.post<{ user: User; token: string }>('/auth/verify-otp', {
      user_id: userId,
      otp_code: otpCode,
    }),
  register: (data: any, recaptchaToken?: string | null) =>
    api.post<{ user: User; token: string }>('/auth/register', {
      ...data,
      ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
    }),
  me: () => api.get<{ user: User }>('/auth/me'),
  refresh: () => api.post<{ token: string }>('/auth/refresh'),
  impersonate: (user_id: number) =>
    api.post<{ user: User; token: string }>('/auth/impersonate', { user_id }),
  exitImpersonate: () => api.post<{ user: User; token: string }>('/auth/exit-impersonate'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
};

// User API
export const userApi = {
  list: (params?: any) => api.get<{ users: User[] }>('/users', params),
  search: (query: string) => api.get<{ users: User[] }>('/user-search', { q: query }),
  create: (data: any) => api.post<{ user: User }>('/users', data),
  update: (id: number, data: any) => api.put<{ user: User }>(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
  updateProfile: (data: any) => api.put<{ profile: User }>('/profile', data),
  updatePassword: (data: any) => api.put<{ profile: User }>('/profile', {
    current_password: data.current_password,
    password: data.new_password, // Backend expects 'password', not 'new_password'
  }),
};

// Profile API
export const profileApi = {
  get: () => api.get<{ user: User }>('/profile'),
  getUser: (id: number) => api.get<{ user: User }>(`/profile/${id}`),
  update: (data: any) => api.put<{ user: User }>('/profile', data),
  uploadDocument: (file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    return api.upload('/profile/upload-document', formData);
  },
  getDocuments: () => api.get('/profile/documents'),
};

// Barter API
export const barterApi = {
  list: (params?: any) => api.get<{ barters: Barter[] }>('/barters', params),
  get: (id: number) => api.get<{ barter: Barter }>(`/barters/${id}`),
  create: (data: any) => api.post<{ barter: Barter }>('/barters', data),
  update: (id: number, data: any) => api.put<{ barter: Barter }>(`/barters/${id}`, data),
  delete: (id: number) => api.delete(`/barters/${id}`),
  getDraft: () => api.get<{ draft: Barter | null }>('/barters/draft'),
  saveDraft: (data: any) => api.post<{ draft_id: number }>('/barters/draft', data),
  deleteDraft: () => api.delete('/barters/draft'),
};

// Barter Search Preferences API
export const barterSearchPreferencesApi = {
  get: () => api.get<{ preferences: any; is_default?: boolean }>('/barter-search-preferences'),
  save: (data: any) => api.put<{ preferences: any }>('/barter-search-preferences', data),
  delete: () => api.delete('/barter-search-preferences'),
};

// Barter Group API
export const barterGroupApi = {
  list: () => api.get<{ groups: BarterGroup[] }>('/barter-groups'),
  get: (id: number) => api.get<{ group: BarterGroup }>(`/barter-groups/${id}`),
  updateStatus: (id: number, status: string) =>
    api.put<{ group: BarterGroup }>(`/barter-groups/${id}`, { status }),
};

// Barter Request API
export const barterRequestApi = {
  list: () => api.get('/barter-requests'),
  create: (data: any) => api.post('/barter-requests', data),
  update: (id: number, status: string) =>
    api.put(`/barter-requests/${id}`, { status }),
};

// Barter Coin API
export const barterCoinApi = {
  purchase: (amount: number) => api.post('/barter-coins/purchase', { amount }),
  transfer: (email: string, amount: number) =>
    api.post('/barter-coins/transfer', { recipient_email: email, amount }),
  withdraw: (amount: number) => api.post('/barter-coins/withdraw', { amount }),
  getBalance: () => api.get('/barter-coins/balance'),
  getTransactions: () => api.get<{ transactions: Transaction[] }>('/transactions'),
};

// Transaction API
export const transactionApi = {
  list: () => api.get<{ transactions: Transaction[] }>('/transactions'),
};

// Message API
export const messageApi = {
  getChannel: (channel: string) => api.get('/messages/channel', { channel }),
  sendChannel: (channel: string, message: string, recipient_id?: number) =>
    api.post('/messages/channel', { channel, message, recipient_id }),
  getGroup: (group_id: number) => api.get('/messages/group', { group_id }),
  sendGroup: (group_id: number, message: string) =>
    api.post('/messages/group', { group_id, message }),
  getDirect: (user_id?: number, barter_id?: number, context_barter_id?: number) =>
    api.get('/messages/direct', barter_id ? { barter_id } : context_barter_id ? { user_id, context_barter_id } : { user_id }),
  sendDirect: (receiver_id: number, message: string, barter_id?: number) =>
    api.post('/messages/direct', { receiver_id, message, barter_id }),
};

// Review API
export const reviewApi = {
  list: (params?: { reviewee_id?: number; barter_id?: number }) =>
    api.get<{ reviews: Review[] }>('/reviews', params),
  create: (data: any) => api.post<{ review: Review }>('/reviews', data),
  searchUsers: (q: string) => api.get<{ users: User[] }>('/reviews/search-users', { q }),
  searchBarters: (q: string) => api.get<{ barters: Barter[] }>('/reviews/search-barters', { q }),
};

// Support API
export const supportApi = {
  list: (params?: any) => api.get<{ tickets: SupportTicket[]; total: number; page: number; limit: number; pages: number }>('/support', params),
  get: (id: number) => api.get<{ ticket: SupportTicket }>(`/support/${id}`),
  create: (data: any) => api.post<{ ticket: SupportTicket }>('/support', data),
  reply: (id: number, message: string) => api.post(`/support/${id}/reply`, { message }),
  resolve: (id: number) => api.post(`/support/${id}/resolve`, {}),
};

// Support Ticket Images API
export const supportTicketImagesApi = {
  list: (ticketId: number) => api.get<{ images: any[] }>('/support-ticket-images', { ticket_id: ticketId }),
  upload: (_ticketId: number, formData: FormData) => api.upload<{ image: any }>('/support-ticket-images', formData),
  delete: (imageId: number) => api.delete(`/support-ticket-images/${imageId}`),
};

// Settings API
export const settingsApi = {
  get: () => api.get<{ settings: SystemSettings }>('/settings'),
  update: (data: any) => api.put<{ settings: SystemSettings }>('/settings', data),
};

// Maintenance Mode API
export const maintenanceApi = {
  get: () => api.get<{ maintenance: any; is_in_maintenance: boolean }>('/maintenance'),
  update: (data: any) => api.put<{ maintenance: any }>('/maintenance', data),
};

// Page Notifications API
export const pageNotificationsApi = {
  list: (params?: any) => api.get<{ notifications: any[]; total?: number; page?: number; limit?: number; pages?: number }>('/page-notifications', params),
  getByPage: (pageRoute: string) => api.get<{ notifications: any[] }>('/page-notifications', { page_route: pageRoute }),
  create: (data: any) => api.post<{ notification: any }>('/page-notifications', data),
  update: (id: number, data: any) => api.put<{ notification: any }>(`/page-notifications/${id}`, data),
  delete: (id: number) => api.delete(`/page-notifications/${id}`),
};

// Location API
export const locationApi = {
  getContinents: () => api.get<{ continents: string[] }>('/locations/continents'),
  getCountries: (continent: string) =>
    api.get<{ countries: string[] }>('/locations/countries', { continent }),
  getStates: (continent: string, country: string) =>
    api.get<{ states: string[] }>('/locations/states', { continent, country }),
  getCities: (continent: string, country: string, state: string) =>
    api.get<{ cities: string[] }>('/locations/cities', { continent, country, state }),
};

// Category API
export const categoryApi = {
  list: () => api.get<{ categories: string[] }>('/categories'),
};

// Dashboard API
export const dashboardApi = {
  get: () => api.get<DashboardData>('/dashboard'),
};

// Subscription API
export const subscriptionApi = {
  createCheckout: () => api.post('/subscriptions/create-checkout'),
  getStatus: () => api.get('/subscriptions/status'),
  cancel: () => api.post('/subscriptions/cancel'),
  reactivate: () => api.post('/subscriptions/reactivate'),
  openPortal: () => api.post('/subscriptions/portal'),
};

// Billing API
export const billingApi = {
  getHistory: (viewAll?: boolean) => api.get('/billing/history', viewAll ? { view_all: 'true' } : {}),
};

// Email Notification API
export const emailNotificationApi = {
  list: () => api.get('/email-notifications'),
  toggle: (id: number, is_enabled: boolean) =>
    api.put(`/email-notifications/${id}`, { is_enabled }),
};

// Withdrawal API
export const withdrawalApi = {
  list: (status?: string) => api.get('/withdrawals', status ? { status } : {}),
  approve: (transaction_id: number, admin_note?: string) =>
    api.post('/withdrawals/approve', { transaction_id, ...(admin_note && { admin_note }) }),
  reject: (transaction_id: number, reason?: string) =>
    api.post('/withdrawals/reject', { transaction_id, reason }),
};

// Escrow API
export const escrowApi = {
  list: (params?: any) => api.get('/escrow/list', params),
  getGroup: (group_id: number) => api.get('/escrow/group', { group_id }),
  transfer: (data: any) => api.post('/escrow/transfer', data),
  returnAll: (data: any) => api.post('/escrow/return-all', data),
};

// Admin Statistics API
export const adminStatisticsApi = {
  get: () => api.get<{
    users: any;
    subscriptions: any;
    barters: any;
    groups: any;
    financial: any;
    requests: any;
    reviews: any;
    support: any;
    database: any;
    messages: any;
    system_health: any;
    growth: any;
    generated_at: string;
  }>('/admin-statistics'),
};

// Coupon API
export const couponApi = {
  list: (params?: any) => api.get<{ coupons: any[]; total?: number; page?: number; limit?: number; pages?: number }>('/coupons', params),
  get: (id: number) => api.get<{ coupon: any; usage: any[] }>(`/coupons/${id}`),
  create: (data: any) => api.post<{ coupon: any }>('/coupons', data),
  update: (id: number, data: any) => api.put<{ coupon: any }>(`/coupons/${id}`, data),
  delete: (id: number) => api.delete(`/coupons/${id}`),
};

// Apply Coupon API
export const applyCouponApi = {
  apply: (coupon_code: string) => api.post('/apply-coupon', { coupon_code }),
  getActive: () => api.get<{ coupons: any[] }>('/apply-coupon'),
  remove: (id: number) => api.delete(`/apply-coupon/${id}`),
};

// Data Purge API
export const dataPurgeApi = {
  verifyPassword: (password: string) => api.post<{ verified: boolean; token: string }>('/data-purge/verify-password', { password }),
  getRecordTypes: () => api.get<{ record_types: Record<string, any> }>('/data-purge'),
  preview: (record_type: string, purge_date: string, purgeToken: string) =>
    api.post<{ record_type: string; purge_date: string; counts: Record<string, number> }>('/data-purge', {
      record_type,
      purge_date,
      purge_token: purgeToken,
    }),
  execute: (record_type: string, purge_date: string, confirmation: string, purgeToken: string) =>
    api.delete<{ total_deleted: number }>('/data-purge', { record_type, purge_date, confirmation, purge_token: purgeToken }),
};

// Audit Logs API
export const auditLogsApi = {
  list: (params?: any) => api.get<{ logs: any[]; action_names: any[]; pagination: any }>('/audit-logs', params),
  delete: (id: number) => api.delete(`/audit-logs/${id}`),
};
